/*
 * fs-community.js — plays the wall without setting fire to the phone.
 *
 * THE PROBLEM THIS EXISTS FOR
 * Thirty <video> elements with `autoplay` all begin decoding at once. On a
 * mid-range Android that is the end of the page: the scroll stutters, the
 * videos stall, and the fans come on. The markup therefore ships with NO
 * autoplay attribute and `preload="none"` — nothing loads until this file says
 * so, and if this file never runs the wall is a grid of poster images, which
 * is a perfectly good page.
 *
 * THREE RULES, IN ORDER
 *   1. a clip is only ever loaded once it is near the screen
 *   2. a clip only plays while it is ON the screen
 *   3. at most `data-fs-comm-max` play at any moment — when more are visible
 *      than that, the ones nearest the middle of the screen win
 *
 * Rule 3 is the one that is easy to skip and expensive to skip. On a desktop a
 * tall mosaic can have twelve tiles in view at once.
 *
 * `prefers-reduced-motion` stops all of it: posters only, nothing moves.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-fs-comm]').forEach(function (wall) {
    var videos = Array.prototype.slice.call(wall.querySelectorAll('[data-fs-comm-video]'));
    if (!videos.length || REDUCED) return;

    var max = Number(wall.getAttribute('data-fs-comm-max')) || 6;
    var hovered = null;   /* declared here because settle() reads it */
    var visible = new Set();
    var frame = null;

    /* `src` is set the first time a clip comes near, and never unset. Removing
       it again would re-download the file on the next pass. */
    function load(v) {
      if (v.dataset.loaded) return;
      var src = v.getAttribute('data-src');
      if (!src) return;
      v.src = src;
      v.dataset.loaded = '1';
    }

    function distanceFromMiddle(v) {
      var r = v.getBoundingClientRect();
      return Math.abs((r.top + r.bottom) / 2 - window.innerHeight / 2);
    }

    /* Called on scroll through rAF rather than directly: an IntersectionObserver
       on thirty tiles fires in bursts, and re-sorting on every entry is work
       done many times for one answer. */
    function settle() {
      frame = null;
      var list = Array.prototype.slice.call(visible);
      list.sort(function (a, b) { return distanceFromMiddle(a) - distanceFromMiddle(b); });

      /* The clip under the pointer always plays, whatever the limit says. */
      if (hovered && list.indexOf(hovered) > -1) {
        list.splice(list.indexOf(hovered), 1);
        list.unshift(hovered);
      }

      list.forEach(function (v, i) {
        if (i < max) {
          load(v);
          if (v.paused) {
            var p = v.play();
            /* A rejected play() is normal — the tab is hidden, or the browser
               has decided not to. Swallow it; an unhandled rejection here would
               be thirty console errors on one scroll. */
            if (p && p.catch) p.catch(function () {});
          }
        } else if (!v.paused) {
          v.pause();
        }
      });

      videos.forEach(function (v) {
        if (v !== hovered && !visible.has(v) && !v.paused) v.pause();
      });
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(settle);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          visible.add(entry.target);
          load(entry.target);          /* start fetching a little before it is needed */
        } else {
          visible.delete(entry.target);
          if (!entry.target.paused) entry.target.pause();
        }
      });
      schedule();
    }, { rootMargin: '200px 0px', threshold: 0.15 });

    videos.forEach(function (v) { io.observe(v); });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    /* A wall left playing on a background tab is decoding video nobody can see. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) videos.forEach(function (v) { if (!v.paused) v.pause(); });
      else schedule();
    });

    /*
     * HOVER: sound on, and it keeps playing while the pointer is there.
     *
     * ⚠️ `closest()` WALKS ANCESTORS, AND THE VIDEO IS A CHILD.
     * This was `e.target.closest('[data-fs-comm-video]')`, which only ever
     * matched when the pointer happened to land on the <video> itself. Entering
     * a tile over its rounded corner, its caption or the gradient above it
     * fires on the link or the media box instead, `closest` finds nothing from
     * there, and the hover silently does nothing — which is exactly what "works
     * on some tiles and not others" looks like.
     *
     * It listens on the TILE and searches DOWN for the video instead.
     *
     * The hovered clip is also pinned: `settle()` pauses anything past the
     * playing limit, and on a wall where six are already running the seventh —
     * the one under the pointer — was started and paused again on the next
     * scroll frame.
     */
    function tileVideo(el) {
      var tile = el && el.closest && el.closest('.fs-comm__tile');
      return tile ? tile.querySelector('[data-fs-comm-video]') : null;
    }

    wall.addEventListener('pointerover', function (e) {
      var v = tileVideo(e.target);
      if (!v || v === hovered) return;
      if (hovered && hovered !== v) hovered.muted = true;
      hovered = v;
      load(v);
      v.muted = false;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });

    wall.addEventListener('pointerout', function (e) {
      var v = tileVideo(e.target);
      /* pointerout also fires moving BETWEEN children of the same tile. Only
         act when the pointer has actually left this tile. */
      if (!v || v !== hovered) return;
      if (e.relatedTarget && tileVideo(e.relatedTarget) === v) return;
      v.muted = true;
      hovered = null;
      schedule();
    });
  });
})();
